'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, type RefObject } from 'react';
import { ProfilePicturePlaceholder } from '@module/mdrt-org/shared/components/profile-picture-placeholder/profile-picture-placeholder';
import { useGlobalCmsContentContext } from '@module/mdrt-org/shared/components/providers/global-cms-context';
import { Link } from '@module/mdrt-org/shared/i18n/i18n-navigation-link';
import { MembershipLevel } from '@module/mdrt-org/shared/utils/enums/membership';
import { getQueryParametersStringFromObject } from '@module/mdrt-org/shared/utils/helpers/getQueryParametersStringFromObject';
import { type Designation } from '@module/mdrt-org/shared/utils/types/designation';
import { MemberCard } from '@shared/ui-components/card/member-card';
import { BadgeCot, BadgeMember, BadgeTot } from '@shared/ui-components/svgs';
import { mapDesignationIdsToLabels } from '@shared/utils/helpers/map-designation-ids-to-labels';
import { type SearchMembersResponse } from '../../utils/types';
import { type MemberDirectoryPageContent } from '../get-member-directory-page-content';
import styles from './search-results.module.scss';
type SearchResultPerson = SearchMembersResponse['users'][number];
const SearchResultCard = t0 => {
  const $ = _c(35);
  const {
    cmsContent,
    designations,
    href,
    person,
    testId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <BadgeCot />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== cmsContent.cotLevelLabel) {
    t2 = <span title={cmsContent.cotLevelLabel}>{t1}</span>;
    $[1] = cmsContent.cotLevelLabel;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <BadgeMember />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== cmsContent.mdrtLevelLabel) {
    t4 = <span title={cmsContent.mdrtLevelLabel}>{t3}</span>;
    $[4] = cmsContent.mdrtLevelLabel;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <BadgeTot />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== cmsContent.totLevelLabel) {
    t6 = <span title={cmsContent.totLevelLabel}>{t5}</span>;
    $[7] = cmsContent.totLevelLabel;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t2 || $[10] !== t4 || $[11] !== t6) {
    t7 = {
      [MembershipLevel.COT]: t2,
      [MembershipLevel.MDRT]: t4,
      [MembershipLevel.TOT]: t6
    };
    $[9] = t2;
    $[10] = t4;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const membershipBadges = t7;
  let t8;
  if ($[13] !== cmsContent.cotLevelLabel || $[14] !== cmsContent.mdrtLevelLabel || $[15] !== cmsContent.totLevelLabel) {
    t8 = {
      [MembershipLevel.COT]: cmsContent.cotLevelLabel,
      [MembershipLevel.MDRT]: cmsContent.mdrtLevelLabel,
      [MembershipLevel.TOT]: cmsContent.totLevelLabel
    };
    $[13] = cmsContent.cotLevelLabel;
    $[14] = cmsContent.mdrtLevelLabel;
    $[15] = cmsContent.totLevelLabel;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const membershipLabels = t8;
  const membershipLevel = person.membershipLevel as MembershipLevel | null;
  const firstName = person.nickname || person.firstName || "";
  const fullName = firstName ? `${firstName} ${person.lastName}` : person.lastName;
  const t9 = membershipLevel ? membershipBadges[membershipLevel] : undefined;
  const t10 = person.companyName ?? undefined;
  let t11;
  if ($[17] !== designations || $[18] !== person.designations) {
    t11 = person?.designations?.length > 0 ? mapDesignationIdsToLabels(person.designations, designations)?.join(", ") : undefined;
    $[17] = designations;
    $[18] = person.designations;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = person.pictureUri ? `data:image/jpeg;base64, ${person.pictureUri}` : undefined;
  const t13 = membershipLevel ? `${membershipLabels[membershipLevel]} ${cmsContent.membershipLevelLabel}` : undefined;
  let t14;
  if ($[20] !== firstName || $[21] !== person.lastName) {
    t14 = <ProfilePicturePlaceholder firstName={firstName} lastName={person.lastName} />;
    $[20] = firstName;
    $[21] = person.lastName;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== fullName || $[24] !== t10 || $[25] !== t11 || $[26] !== t12 || $[27] !== t13 || $[28] !== t14 || $[29] !== t9) {
    t15 = <MemberCard badge={t9} companyName={t10} designations={t11} imageSrc={t12} levelLabel={t13} name={fullName} placeholder={t14} />;
    $[23] = fullName;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
    $[29] = t9;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== href || $[32] !== t15 || $[33] !== testId) {
    t16 = <Link className={styles["person-card-link"]} data-testid={testId} href={href}>{t15}</Link>;
    $[31] = href;
    $[32] = t15;
    $[33] = testId;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  return t16;
};
export const SearchResults = t0 => {
  const $ = _c(18);
  const {
    cmsContent,
    designations,
    resultsListHeaderRef,
    searchResults
  } = t0;
  const {
    cmsPaths: t1
  } = useGlobalCmsContentContext();
  const {
    memberDirectoryProfilePath
  } = t1;
  let t2;
  if ($[0] !== cmsContent.resultsSectionTitle || $[1] !== resultsListHeaderRef || $[2] !== searchResults.count) {
    t2 = <h2 className={styles["results-list-header"]} ref={resultsListHeaderRef}>{cmsContent.resultsSectionTitle} {searchResults.count}</h2>;
    $[0] = cmsContent.resultsSectionTitle;
    $[1] = resultsListHeaderRef;
    $[2] = searchResults.count;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== cmsContent || $[5] !== designations || $[6] !== memberDirectoryProfilePath || $[7] !== searchResults.users) {
    let t4;
    if ($[9] !== cmsContent || $[10] !== designations || $[11] !== memberDirectoryProfilePath) {
      t4 = (person, index) => <SearchResultCard cmsContent={cmsContent} designations={designations} href={`${memberDirectoryProfilePath}?${getQueryParametersStringFromObject({
        mdrtId: person.id
      })}`} key={person.id} person={person} testId={`person-card-${index}`} />;
      $[9] = cmsContent;
      $[10] = designations;
      $[11] = memberDirectoryProfilePath;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    t3 = searchResults.users.map(t4);
    $[4] = cmsContent;
    $[5] = designations;
    $[6] = memberDirectoryProfilePath;
    $[7] = searchResults.users;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[13] !== t3) {
    t4 = <div className={styles["results-list"]}>{t3}</div>;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== t2 || $[16] !== t4) {
    t5 = <>{t2}{t4}</>;
    $[15] = t2;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
};